import { Box } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { sessionService } from "redux-react-session";

import { loadingMsg, receive, send } from "../messages";
import "../../assets/wallet.scss";
import "../../assets/verify-number.scss";
import WalletAmountBox from "../WalletAmountBox";
import Button from "../button/Button";
import ReceiveOptions from "./ReceiveOptions";
import SendOptions from "./SendOptions";
import SetupLnAddressOptions from "./SetupLnAddressOptions";
import {
  callAPIInterface,
  fetchCountries,
  getCountryObj,
  isPhoneVerificationRequired,
  lightningLabel,
  lnAddressConst,
  paymentConst,
  swapConst,
  walletConst,
  withdrawConst,
  getSwapLimits,
  supportedTaprootAssets,
  hasPaymentMethodWithModule,
  isMFAVerificationRequired,
} from "../constants";
import PendingTransactionCount from "../PendingTransactionCount";
import { useSession } from "../../hooks/useSession";
import BackdropLoader from "../backdropLoader/BackdropLoader";
import CommonHeader from "../CommonHeader";
import { setCurrentAccountName } from "../../redux/actions";
import CommonDrawer from "../drawer/CommonDrawer";
import VerifyPhoneNumberBar from "../verifyPhoneNumber/VerifyPhoneNumberBar";
import ToggleBalanceComponent from "./ToggleBalanceComponent";
import VerifyMFABar from "../verifyMFA/VerifyMFABar";

function Wallet({ isIosDevice }) {
  const { session } = useSession();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { accountAssets, taprootAsset, currentAccountName, sendToContactData } =
    useSelector((state) => state.common);

  const [isReceive, setIsReceive] = useState(false);
  const [isSend, setIsSend] = useState(false);
  const [isOpenLnAddressModal, setIsOpenLnAddressModal] = useState(false);
  const [isButtonDisabled, setButtonDisabled] = useState(false);
  const [isSupportedCurrency, setIsSupportedCurrency] = useState(true);
  const [isLoader, setIsLoader] = useState(false);
  const [lnAddress, setLnAddress] = useState();
  const [sessionLoader, setSessionLoader] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [statesAvailable, setStatesAvailable] = useState(false);
  const [isMobileVerificationRequired, setIsMobileVerificationRequired] =
    useState(false);
  const [countries, setCountries] = useState([]);
  const [daysLeft, setDaysLeft] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [isMFARequired, setIsMFARequired] = useState(false);
  const [countrySupportedMFA, setCountrySupportedMFA] = useState(false);
  const [mfaStatus, setMFAStatus] = useState(false);

  const account = session?.account;
  const { uuid: currency } = { ...taprootAsset };
  const isModuleSupportedInWallet =
    selectedAsset?.supported_account_type?.includes(walletConst);
  const isShowReceiveButton =
    isModuleSupportedInWallet &&
    hasPaymentMethodWithModule({ asset: selectedAsset, module: paymentConst });
  const isShowSendButton =
    isModuleSupportedInWallet &&
    hasPaymentMethodWithModule({ asset: selectedAsset, module: withdrawConst });
  const isLnAddressSupported =
    isModuleSupportedInWallet &&
    selectedAsset?.supported_modules?.includes(lnAddressConst);
  const hasLightningWithPayRequest =
    isLnAddressSupported &&
    selectedAsset?.payment_method_details?.some(
      (method) =>
        method.payment_method_name === lightningLabel &&
        method.supported_modules?.includes(lnAddressConst)
    );
  const [swapLimits, setSwapLimits] = useState([]);

  const { supportedSwaps, isShowSwapButton } = useMemo(() => {
    const supportedSwaps = swapLimits?.filter(
      (swapLimit) =>
        swapLimit.target_currency_swap_in === currency &&
        supportedTaprootAssets.includes(swapLimit.target_currency_swap_out) &&
        accountAssets
          .find((asset) => swapLimit.target_currency_swap_out === asset.uuid)
          ?.supported_modules?.includes(swapConst)
    );
    return {
      isShowSwapButton:
        isModuleSupportedInWallet &&
        supportedSwaps.length &&
        selectedAsset?.supported_modules?.includes(swapConst),
      supportedSwaps,
    };
  }, [swapLimits, currency]);

  const handleSend = () => {
    setIsSend(true);
  };

  const fetchSwapLimits = async () => {
    try {
      const limitResponse = await getSwapLimits();
      setSwapLimits(limitResponse);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (account?.account?.country) {
      checkStatesAvailable();
    }

    if (session) {
      fetchSwapLimits();
      setSessionLoader(false);
      dispatch(setCurrentAccountName(session?.account?.account));
    }
    window.scrollTo(0, 0);
  }, [session]);

  useEffect(() => {
    if (session && countries?.length) {
      calculateDaysLeft();
    }
  }, [session, countries]);

  useEffect(() => {
    if (account?.account?.country && hasLightningWithPayRequest) {
      fetchLnAddress();
    }
  }, [account, currency, hasLightningWithPayRequest]);

  useEffect(() => {
    if (session && countries) {
      const isMobileVerificationRequired = isPhoneVerificationRequired(
        session?.user,
        countries
      );
      setIsMobileVerificationRequired(isMobileVerificationRequired);
    }
  }, [session, countries]);

  useEffect(() => {
    getCurrentSession();
  }, []);

  const calculateDaysLeft = () => {
    const accountCountryObj = getCountryObj(
      "short_name",
      account?.account?.country,
      countries
    );
    const now = Date.now(); // current time in milliseconds

    const msInDay = 1000 * 60 * 60 * 24;
    const daysLeft = Math.round(
      (accountCountryObj?.phone_number_verification_starts_at - now) / msInDay
    );
    setDaysLeft(daysLeft > 0 && daysLeft);
  };

  useEffect(() => {
    const { payment_address_id, payment_address } = sendToContactData;
    if (payment_address_id && payment_address) {
      setTimeout(() => {
        handleSend();
      }, 500);
    }
  }, [sendToContactData]);

  useEffect(() => {
    const isMFARequired = isMFAVerificationRequired(
      account,
      mfaStatus,
      countrySupportedMFA
    );

    setIsMFARequired(!!isMFARequired);
  }, [session, mfaStatus, countrySupportedMFA]);

  useEffect(() => {
    if (session) {
      mfaStatusAPI();
    }
  }, [session]);

  const fetchLnAddress = async () => {
    try {
      setIsLoader(true);
      const apiData = {
        status: "active",
        currency,
      };
      const paymentAddressRes = await callAPIInterface(
        "POST",
        "/payment-addresses/filter?limit=1",
        JSON.stringify(apiData)
      );
      if (paymentAddressRes) {
        setIsLoader(false);
        setLnAddress(paymentAddressRes?.data?.[0]);
      }
    } catch (err) {
      setIsLoader(false);
    }
  };

  const checkStatesAvailable = async () => {
    try {
      const fetchedCountries = await fetchCountries();
      setCountries(fetchedCountries);
      const currentCountry = fetchedCountries?.find(
        (country) => country.short_name === account?.account?.country
      );
      setStatesAvailable(!!currentCountry?.states);
      setCountrySupportedMFA(
        !!(
          currentCountry?.is_google_auth_supported ||
          currentCountry?.is_twillio_supported
        )
      );
    } catch (error) {}
  };

  const mfaStatusAPI = async () => {
    try {
      const mfaStatus = await callAPIInterface("GET", "/mfa");

      const isAnyMFAEnabled = mfaStatus?.data?.some(
        (item) =>
          item.is_google_auth_enabled || item.is_mfa_phone_number_verified
      );

      setMFAStatus(!!isAnyMFAEnabled);
    } catch (error) {}
  };

  const handleReceive = () => {
    setIsReceive(true);
  };

  const getCurrentSession = async () => {
    const session = await sessionService.loadSession();
    setCurrentSession(session);
  };

  const handleSettings = () => setIsDrawerOpen(true);

  const renderLnAddressSetupContent = (
    <SetupLnAddressOptions
      isOpenModal={isOpenLnAddressModal}
      setIsOpenModal={setIsOpenLnAddressModal}
      handleFetchLnAddress={fetchLnAddress}
      isIosDevice={isIosDevice}
    />
  );

  const toggleComponentProps = {
    currency: currency,
    currentSession: currentSession,
    setCurrentSession: setCurrentSession,
    isDrawerOpen: isDrawerOpen,
    setIsDrawerOpen: setIsDrawerOpen,
  };

  return sessionLoader ? (
    <BackdropLoader
      open={sessionLoader}
      text={loadingMsg}
      customClass="loading-in-auth"
    />
  ) : !account?.account?.country ||
    (!account?.account?.state && statesAvailable) ? (
    navigate("/add-country", { state: { walletAccounts: [account] } })
  ) : (
    <>
      <CommonHeader
        icon="speedIcon"
        textAfterIcon={currentAccountName}
        showMaintenanceBar={true}
        showPlan={true}
      />
      <Box className="options-container">
        <Box className="wallet-wrapper">
          <Box className="wallet-container">
            {isMobileVerificationRequired && daysLeft <= 30 && (
              <VerifyPhoneNumberBar
                countries={countries}
                navigate={navigate}
                daysLeft={daysLeft}
              />
            )}
            {isMFARequired && <VerifyMFABar navigate={navigate} />}
            <WalletAmountBox
              isFromBrowser={true}
              setButtonDisabled={setButtonDisabled}
              setIsSupportedCurrency={setIsSupportedCurrency}
              handleOpenReceiveModal={handleReceive}
              setSelectedAsset={setSelectedAsset}
              isShowSwapButton={isShowSwapButton}
              handleSettings={handleSettings}
              currentSession={currentSession}
              session={session}
              supportedSwaps={supportedSwaps}
            />
            {/**Create/Show LN Address and show pending transaction count section */}
            <PendingTransactionCount
              isFromBrowser={true}
              lnAddress={lnAddress}
              setIsOpenModal={setIsOpenLnAddressModal}
              isOpenModal={isOpenLnAddressModal}
              lnLoader={isLoader}
              isLnAddressSupported={hasLightningWithPayRequest}
            />

            {isReceive && (
              <ReceiveOptions
                isReceive={isReceive}
                setIsReceive={(val) => {
                  setIsReceive(val);
                }}
              />
            )}
            {isSend && (
              <SendOptions
                isSend={isSend}
                setIsSend={(val) => {
                  setIsSend(val);
                }}
              />
            )}
          </Box>
          <Box className="wallet-buttons-wrapper">
            {isShowReceiveButton && (
              <Button
                fullWidth
                variant={`wallet-outlined-${currency}`}
                className="send-receive-btn"
                iconPosition="start"
                label={receive}
                onClick={handleReceive}
                disabled={isButtonDisabled || !isSupportedCurrency}
              />
            )}
            {isShowSendButton && (
              <Button
                fullWidth
                variant={`wallet-contained-${currency}`}
                className="send-receive-btn"
                iconPosition="start"
                label={send}
                icon="sendIcon"
                onClick={() => {
                  setIsSend(true);
                }}
                disabled={isButtonDisabled || !isSupportedCurrency}
              />
            )}

            <CommonDrawer
              anchor="bottom"
              open={isOpenLnAddressModal}
              onClose={() => setIsOpenLnAddressModal(false)}
              className={"ln-address-drawer"}
            >
              {renderLnAddressSetupContent}
            </CommonDrawer>
            <ToggleBalanceComponent {...toggleComponentProps} />
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default Wallet;
